import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { registerFormValidationErrorType } from 'modules/general/enums';
import generalStrings from 'modules/general/strings';
import DropdownMenu from 'modules/common/components/DropdownMenu';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  FormGrid,
  FormLabel,
  FormTextInput,
  FormFieldWrapper,
  FormValidationError,
} from './CompanyInformation.style';

const CompanyInformation = ({
  formFieldsValues,
  formFieldsValidation,
  handleTextInputFieldChange,
  handleDropdownFieldChange,
}) => {
  const { citiesList } = useSelector(state => ({ citiesList: state.common.citiesList }));

  const allCities = useMemo(
    () =>
      citiesList.details.map(city => ({
        key: city.Key,
        name: city.Name,
      })),
    [citiesList],
  );
  const allAreas = useMemo(
    () =>
      (
        (formFieldsValues.companyCity &&
          citiesList.details.find(city => city.Key === formFieldsValues.companyCity).Areas) ||
        []
      ).map(area => ({
        key: area.Key,
        name: area.Name,
      })),
    [citiesList, formFieldsValues.companyCity],
  );

  return (
    <FormGrid>
      {/* Company Name Field */}
      <FormFieldWrapper span={6}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyNameFieldLabel}*`}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="text"
          value={formFieldsValues.companyName}
          onChange={event => handleTextInputFieldChange('companyName', event)}
        />
        {formFieldsValidation.companyName === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyNameFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company City Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyCityFieldLabel}*`}</FormLabel>
        <SectionDivider gap={8} />
        <DropdownMenu
          dropdownMenuItemsList={allCities}
          dropdownMenuSelectedObj={
            (formFieldsValues.companyCity && {
              key: formFieldsValues.companyCity,
              name: formFieldsValues.companyCityName,
            }) ||
            {}
          }
          dropdownMenuPlaceholder={generalStrings.registerForm.formCompanyCityFieldPlaceholder}
          showLoader={false}
          handleDropdownMenuChange={value => handleDropdownFieldChange('companyCity', value)}
        />
        {formFieldsValidation.companyCity === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyCityFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Area Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyAreaFieldLabel}*`}</FormLabel>
        <SectionDivider gap={8} />
        <DropdownMenu
          dropdownMenuItemsList={allAreas}
          dropdownMenuSelectedObj={
            (formFieldsValues.companyArea && {
              key: formFieldsValues.companyArea,
              name: formFieldsValues.companyAreaName,
            }) ||
            {}
          }
          dropdownMenuPlaceholder={generalStrings.registerForm.formCompanyAreaFieldPlaceholder}
          disabled={allAreas.length === 0}
          showLoader={false}
          handleDropdownMenuChange={value => handleDropdownFieldChange('companyArea', value)}
        />
        {formFieldsValidation.companyArea === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyAreaFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Location Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyLocationFieldLabel}*`}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="text"
          placeholder={generalStrings.registerForm.formCompanyLocationFieldPlaceholder}
          value={formFieldsValues.companyLocation}
          onChange={event => handleTextInputFieldChange('companyLocation', event)}
        />
        {formFieldsValidation.companyLocation === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyLocationFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Apartment Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyApartmentFieldLabel}*`}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="text"
          placeholder={generalStrings.registerForm.formCompanyApartmentFieldPlaceholder}
          value={formFieldsValues.companyApartment}
          onChange={event => handleTextInputFieldChange('companyApartment', event)}
        />
        {formFieldsValidation.companyApartment === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyCompanyApartmentErrorText}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Email Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyEmailFieldLabel}*`}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="text"
          value={formFieldsValues.companyEmail}
          onChange={event => handleTextInputFieldChange('companyEmail', event)}
        />
        {formFieldsValidation.companyEmail === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyEmailFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
        {formFieldsValidation.companyEmail === registerFormValidationErrorType.wrong && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formWrongFieldErrorText(
                generalStrings.registerForm.formCompanyEmailFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Website Field */}
      <FormFieldWrapper span={3}>
        <FormLabel>{generalStrings.registerForm.formCompanyWebsiteFieldLabel}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="text"
          value={formFieldsValues.companyWebsite}
          onChange={event => handleTextInputFieldChange('companyWebsite', event)}
        />
        {formFieldsValidation.companyWebsite === registerFormValidationErrorType.wrong && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formWrongFieldErrorText(
                generalStrings.registerForm.formCompanyWebsiteFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Mobile Field */}
      <FormFieldWrapper span={2}>
        <FormLabel>{`${generalStrings.registerForm.formCompanyMobileNumberFieldLabel}*`}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="number"
          value={formFieldsValues.companyMobileNumber}
          onChange={event => handleTextInputFieldChange('companyMobileNumber', event)}
        />
        {formFieldsValidation.companyMobileNumber === registerFormValidationErrorType.empty && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formEmptyFieldErrorText(
                generalStrings.registerForm.formCompanyMobileNumberFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
        {formFieldsValidation.companyMobileNumber === registerFormValidationErrorType.wrong && (
          <>
            <SectionDivider gap={4} />
            <FormValidationError>
              {generalStrings.registerForm.formWrongFieldErrorText(
                generalStrings.registerForm.formCompanyMobileNumberFieldLabel,
              )}
            </FormValidationError>
          </>
        )}
      </FormFieldWrapper>

      {/* Company Phone Field */}
      <FormFieldWrapper span={2}>
        <FormLabel>{generalStrings.registerForm.formCompanyPhoneNumberFieldLabel}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="number"
          value={formFieldsValues.companyPhoneNumber}
          onChange={event => handleTextInputFieldChange('companyPhoneNumber', event)}
        />
      </FormFieldWrapper>

      {/* Company Fax Field */}
      <FormFieldWrapper span={2}>
        <FormLabel>{generalStrings.registerForm.formCompanyFaxFieldLabel}</FormLabel>
        <SectionDivider gap={4} />
        <FormTextInput
          type="number"
          value={formFieldsValues.companyFax}
          onChange={event => handleTextInputFieldChange('companyFax', event)}
        />
      </FormFieldWrapper>
    </FormGrid>
  );
};

CompanyInformation.propTypes = {
  formFieldsValues: PropTypes.shape().isRequired,
  formFieldsValidation: PropTypes.shape().isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleDropdownFieldChange: PropTypes.func.isRequired,
};

export default CompanyInformation;
